/**
 * Error Classification System
 * 
 * This module defines all error types used in the application for better
 * error handling, user feedback, and monitoring.
 */

export type ErrorType =
  | 'NETWORK_ERROR'          // Network connectivity issues
  | 'API_ERROR'              // API returned an error response
  | 'NOT_FOUND'              // Resource not found (404)
  | 'UNAUTHORIZED'           // Authentication required (401)
  | 'FORBIDDEN'              // Insufficient permissions (403)
  | 'VALIDATION_ERROR'       // Client-side validation failure
  | 'CONFIG_ERROR'           // Configuration missing or invalid
  | 'RENDERING_ERROR'        // React rendering error
  | 'UNKNOWN_ERROR';         // Unexpected error

export interface ClassifiedError {
  type: ErrorType;
  message: string;
  userMessage: string;        // User-friendly message
  originalError?: Error | unknown;
  statusCode?: number;
  retryable: boolean;         // Can the user retry this action?
  requiresSupport: boolean;   // Should user contact support?
  metadata?: Record<string, unknown>;
}

/**
 * Display label per classification — the one place an `ErrorType` gets a human name.
 *
 * Keyed as `Record<ErrorType, string>` so adding a member to the union fails
 * compilation here until it is named. Every surface that shows a classification to
 * a user reads this: the toast title (GlobalErrorHandler), and the boundary
 * fallbacks. They used to derive their own label with `type.replace(/_/g, ' ')`,
 * which rendered SCREAMING CASE and drifted from the toast wording.
 */
export const ERROR_TITLES: Record<ErrorType, string> = {
  NETWORK_ERROR: 'Network Error',
  API_ERROR: 'API Error',
  NOT_FOUND: 'Not Found',
  UNAUTHORIZED: 'Unauthorized',
  FORBIDDEN: 'Forbidden',
  VALIDATION_ERROR: 'Validation Error',
  CONFIG_ERROR: 'Configuration Error',
  RENDERING_ERROR: 'Rendering Error',
  UNKNOWN_ERROR: 'Unknown Error',
};

/**
 * Classify an error into a specific type with user-friendly messaging
 */
export function classifyError(error: unknown): ClassifiedError {
  // Network errors
  if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('network'))) {
    return {
      type: 'NETWORK_ERROR',
      message: 'Network request failed',
      userMessage: 'Unable to connect to the server. Please check your internet connection and try again.',
      originalError: error,
      retryable: true,
      requiresSupport: false,
    };
  }

  // Connectivity failures that already went through apiClient. It wraps every fetch
  // TypeError into an ApiError before rethrowing, so the TypeError branch above never
  // sees them — and ApiError's constructor assigns `status` unconditionally, so the
  // `'status' in error` branch below matches even when the value is undefined and
  // funnels them into API_ERROR. Duck-typed on `code` rather than importing ApiError,
  // which would drag the UI barrel (apiClient/errors imports butterToast) in here.
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    ((error as { code: unknown }).code === 'NETWORK_ERROR' ||
      (error as { code: unknown }).code === 'TIMEOUT_ERROR')
  ) {
    const userMessage =
      'userMessage' in error && typeof (error as { userMessage: unknown }).userMessage === 'string'
        ? (error as { userMessage: string }).userMessage
        : 'Unable to connect to the server. Please check your internet connection and try again.';

    return {
      type: 'NETWORK_ERROR',
      message: 'message' in error ? String((error as { message: string }).message) : 'Network request failed',
      userMessage,
      originalError: error,
      retryable: true,
      requiresSupport: false,
    };
  }

  // API errors with status codes. `typeof === 'number'` rather than a bare `in` check:
  // an ApiError always *has* `status`, so `in` alone would claim every one of them.
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  ) {
    const statusCode = (error as { status: number }).status;
    const message = 'message' in error ? String((error as { message: string }).message) : 'API request failed';

    switch (statusCode) {
      case 404:
        return {
          type: 'NOT_FOUND',
          message,
          userMessage: 'The requested resource was not found. It may have been removed or moved.',
          originalError: error,
          statusCode,
          retryable: false,
          requiresSupport: false,
        };

      case 401:
        return {
          type: 'UNAUTHORIZED',
          message,
          userMessage: 'Please sign in to continue.',
          originalError: error,
          statusCode,
          retryable: false,
          requiresSupport: false,
        };

      case 403:
        return {
          type: 'FORBIDDEN',
          message,
          userMessage: "You don't have permission to perform this action.",
          originalError: error,
          statusCode,
          retryable: false,
          requiresSupport: true,
        };

      case 422:
        return {
          type: 'VALIDATION_ERROR',
          message,
          userMessage: message || 'Please check your input and try again.',
          originalError: error,
          statusCode,
          retryable: true,
          requiresSupport: false,
        };

      case 500:
      case 502:
      case 503:
        return {
          type: 'API_ERROR',
          message,
          userMessage: 'The server encountered an error. Please try again in a few moments.',
          originalError: error,
          statusCode,
          retryable: true,
          requiresSupport: true,
        };

      default:
        return {
          type: 'API_ERROR',
          message,
          userMessage: message || 'An error occurred while processing your request.',
          originalError: error,
          statusCode,
          retryable: true,
          requiresSupport: false,
        };
    }
  }

  // Configuration errors (club/pricing not configured)
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();
    
    if (
      errorMessage.includes('not configured') ||
      errorMessage.includes('missing pricing') ||
      errorMessage.includes('club pricing') ||
      errorMessage.includes('config')
    ) {
      return {
        type: 'CONFIG_ERROR',
        message: error.message,
        userMessage: 'This club needs additional setup. Please contact the club administrator.',
        originalError: error,
        retryable: false,
        requiresSupport: true,
        metadata: { configType: 'club_setup' },
      };
    }

    // React rendering errors
    if (errorMessage.includes('render') || errorMessage.includes('hook')) {
      return {
        type: 'RENDERING_ERROR',
        message: error.message,
        userMessage: 'A display error occurred. Please refresh the page.',
        originalError: error,
        retryable: true,
        requiresSupport: false,
      };
    }
  }

  // Generic error fallback
  const message = error instanceof Error ? error.message : String(error);
  return {
    type: 'UNKNOWN_ERROR',
    message,
    userMessage: 'An unexpected error occurred. Please try again or contact support if the issue persists.',
    originalError: error,
    retryable: true,
    requiresSupport: true,
  };
}

/**
 * Check if an error is related to club configuration
 */
export function isConfigurationError(error: unknown): boolean {
  const classified = classifyError(error);
  return classified.type === 'CONFIG_ERROR';
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  const classified = classifyError(error);
  return classified.type === 'NETWORK_ERROR';
}

/**
 * Check if an error is a 404
 */
export function isNotFoundError(error: unknown): boolean {
  const classified = classifyError(error);
  return classified.type === 'NOT_FOUND';
}

/**
 * Get a user-friendly error message
 */
export function getUserMessage(error: unknown): string {
  const classified = classifyError(error);
  return classified.userMessage;
}
